import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Plane, CalendarDays, Star } from 'lucide-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from './components/Navigation'
import Locations from './components/Locations'
import Footer from './components/Footer';
import ContactForm from './components/ContactForm';
import PackagesSection from './components/PackagesSection';
import BuildService from './pages/BuildService';
import PortalRedirect from './pages/PortalRedirect';
import EventsPage from './pages/EventsPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';
import PressPage from './pages/PressPage';
import LegalPage from './pages/LegalPage';
import JobPage from './pages/JobPage';

function HomePage() {
  const location = useLocation();

  // Scroll to hash on mount
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location.hash]);

  return (
    <main style={ { backgroundColor: '#0b1a14' }}>
      {/* Hero Section */}
      <header id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/hero-bg.png"
            alt="Luxury Concierge"
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b1a14] via-[#0b1a14]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 text-center px-6">
          <div className="mb-6">
            <span className="text-[#C4A77D] text-xs tracking-[0.4em] uppercase">Premium Concierge Services</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl leading-none mb-6 text-white tracking-tight" style={ { fontFamily: "'Playfair Display', serif" }}>
            Raha-Ídúù
          </h1>
          
          <p className="text-lg md:text-xl text-gray-300 font-light max-w-xl mx-auto mb-10 leading-relaxed">
            Elevating experiences, crafting dreams. Bespoke lifestyle management across Nigeria.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#services"
              className="px-12 py-4 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider transition-all duration-300 bg-[#C4A77D] text-black hover:bg-white hover:border-white"
            >
              Explore Services
            </a>
            <a
              href="#contact"
              className="px-12 py-4 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider transition-all duration-300 hover:bg-[#C4A77D] hover:text-black"
            >
              Book Consultation
            </a>
          </div>

          <div className="mt-16 flex justify-center gap-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C4A77D] rounded-full"></span> 
              Abuja
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C4A77D] rounded-full"></span> 
              Lagos
            </div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C4A77D] rounded-full"></span> 
              Calabar
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C4A77D] to-transparent opacity-50"></div>
        </div>
      </header>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">What We Offer</span>
            <h2 className="text-3xl md:text-5xl mt-4 text-white" style={ { fontFamily: "'Playfair Display', serif" }}>Our Excellence</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="group p-8 border border-white/10 hover:border-[#C4A77D]/30 transition-all duration-500 bg-white/[0.02] hover:bg-white/[0.04]">
              <div className="text-[#C4A77D] mb-6">
                <Plane className="w-8 h-8" />
              </div>
              <h3 className="text-xl text-white mb-3 group-hover:text-[#C4A77D] transition-colors" style={ { fontFamily: "'Playfair Display', serif" }}>Travel</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">Private jets, luxury vehicle fleets, and VIP terminal access.</p>
            </div>

            {/* Card 2 */}
            <div className="group p-8 border border-white/10 hover:border-[#C4A77D]/30 transition-all duration-500 bg-white/[0.02] hover:bg-white/[0.04]">
              <div className="text-[#C4A77D] mb-6">
                <CalendarDays className="w-8 h-8" />
              </div>
              <h3 className="text-xl text-white mb-3 group-hover:text-[#C4A77D] transition-colors" style={ { fontFamily: "'Playfair Display', serif" }}>Events</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">Exclusive gatherings, VIP access, and private celebrations.</p>
            </div>

            {/* Card 3 */}
            <div className="group p-8 border border-white/10 hover:border-[#C4A77D]/30 transition-all duration-500 bg-white/[0.02] hover:bg-white/[0.04]">
              <div className="text-[#C4A77D] mb-6">
                <Star className="w-8 h-8" />
              </div>
              <h3 className="text-xl text-white mb-3 group-hover:text-[#C4A77D] transition-colors" style={ { fontFamily: "'Playfair Display', serif" }}>Lifestyle</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">Personal shopping, wellness retreats, and bespoke services.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <PackagesSection />

      {/* Stats Section */}
      <section className="py-16 bg-[#081210] border-y border-white/5">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl md:text-5xl text-[#C4A77D] mb-2" style={ { fontFamily: "'Playfair Display', serif" }}>3+</div>
            <div className="text-xs tracking-widest text-gray-500 uppercase">Cities</div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl text-[#C4A77D] mb-2" style={ { fontFamily: "'Playfair Display', serif" }}>24/7</div>
            <div className="text-xs tracking-widest text-gray-500 uppercase">Support</div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl text-[#C4A77D] mb-2" style={ { fontFamily: "'Playfair Display', serif" }}>100%</div>
            <div className="text-xs tracking-widest text-gray-500 uppercase">Discretion</div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl text-[#C4A77D] mb-2" style={ { fontFamily: "'Playfair Display', serif" }}>5★</div>
            <div className="text-xs tracking-widest text-gray-500 uppercase">Rating</div>
          </div>
        </div>
      </section>

      <section id="locations">
        <Locations />
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-[#050d09]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">Get in Touch</span>
            <h2 className="text-3xl md:text-5xl mt-4 text-white" style={ { fontFamily: "'Playfair Display', serif" }}>Start Your Journey</h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}

function App() {
  return (
    <Router>
      <div>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/curate-experience" element={<BuildService />} />
          <Route path="/portal/login" element={<PortalRedirect />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/careers/:slug" element={<JobPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App
